import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AdminTabBar from "./AdminTabBar";
import {
  addMasterDataAction,
  deleteMasterDataAction,
  editMasterDataAction,
  fetchMasterDataAction,
} from "../../ustils/actioncreator";
import { useAdminUi } from "../../hooks/useAdminUi";

const MASTER_TABS = [
  { id: "bedrooms", label: "Bedrooms" },
  { id: "bathrooms", label: "Bathrooms" },
  { id: "countries", label: "Countries" },
  { id: "cities", label: "Cities" },
  { id: "localities", label: "Localities" },
  { id: "languages", label: "Languages" },
  { id: "amenities", label: "Amenities" },
  { id: "propertyTypes", label: "Property types" },
];

const PROPERTY_TYPE_TABS = [
  { id: "commercial", label: "Commercial" },
  { id: "residential", label: "Residential" },
];

const EMPTY_FORM = {
  name: "",
  category: "",
  status: "1",
  position: "",
  latitude: "",
  longitude: "",
  country_id: "",
  city_id: "",
};

function extractList(res) {
  if (!res || typeof res !== "object") return [];
  if (Array.isArray(res.data)) return res.data;
  if (res.data && typeof res.data === "object" && Array.isArray(res.data.data)) return res.data.data;
  if (Array.isArray(res.items)) return res.items;
  return [];
}

function extractTotal(res, listLength) {
  const n = Number(res?.totalRecords);
  return Number.isFinite(n) && n >= 0 ? n : listLength;
}

function rowId(item) {
  return item?._id || item?.id || null;
}

function formatStatus(status) {
  if (status === 1 || status === "1") return "Active";
  if (status === 0 || status === "0") return "Inactive";
  return status != null && status !== "" ? String(status) : "—";
}

function formatCoordinates(item) {
  const coords = item?.location?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return "—";
  return `${coords[0]}, ${coords[1]}`;
}

function formatCountryLabel(item) {
  const c = item?.country_id;
  if (c && typeof c === "object") return c.name || "—";
  if (item?.country_name) return item.country_name;
  return "—";
}

function resolveCountryId(item) {
  const c = item?.country_id;
  if (c && typeof c === "object") return c._id || c.id || "";
  if (typeof c === "string") return c;
  return "";
}

function resolveCityId(item) {
  const c = item?.city_id;
  if (c && typeof c === "object") return c._id || c.id || "";
  if (typeof c === "string") return c;
  return "";
}

function formatCityLabel(item) {
  const c = item?.city_id;
  if (c && typeof c === "object") return c.name || "—";
  if (item?.city_name) return item.city_name;
  return "—";
}

function itemToForm(item, resource) {
  if (!item) return { ...EMPTY_FORM };
  const coords = item?.location?.coordinates;
  const needsCountry = resource === "cities" || resource === "localities";
  return {
    name: item?.name ?? "",
    category: resource === "propertyTypes" ? item?.category ?? "" : "",
    status: item?.status != null ? String(item.status) : "1",
    position: item?.position != null ? String(item.position) : "",
    latitude: Array.isArray(coords) && coords[0] != null ? String(coords[0]) : "",
    longitude: Array.isArray(coords) && coords[1] != null ? String(coords[1]) : "",
    country_id: needsCountry ? resolveCountryId(item) : "",
    city_id: resource === "localities" ? resolveCityId(item) : "",
  };
}

function buildPayload(resource, form, editId) {
  const payload = {};
  if (editId) payload._id = editId;

  if (resource === "localities") {
    payload.name = form.name.trim();
    payload.country_id = form.country_id;
    payload.city_id = form.city_id;
    payload.location = {
      coordinates: [Number(form.latitude), Number(form.longitude)],
    };
    return payload;
  }

  payload.name = form.name.trim();
  if (resource === "propertyTypes") {
    payload.category = form.category;
    return payload;
  }
  if (resource === "countries") {
    return payload;
  }
  if (resource === "cities") {
    payload.country_id = form.country_id;
    return payload;
  }
  if (resource === "amenities") {
    payload.position = Number(form.position);
    payload.status = Number(form.status);
    return payload;
  }

  payload.status = Number(form.status);
  return payload;
}

function validateForm(resource, form) {
  if (!form.name.trim()) return "Name is required.";
  if (resource === "propertyTypes" && !form.category) return "Category is required.";
  if (resource === "localities") {
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (!form.country_id) return "Country is required.";
    if (!form.city_id) return "City is required.";
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "Valid latitude and longitude are required.";
  }
  if (resource === "amenities") {
    const pos = Number(form.position);
    if (!Number.isFinite(pos)) return "Position must be a number.";
  }
  if (resource === "cities" && !form.country_id) return "Country is required.";
  return "";
}

function hasStatusField(resource) {
  return !["localities", "propertyTypes", "countries", "cities"].includes(resource);
}

const TAB_META = {
  bedrooms: { title: "Bedrooms", singular: "Bedroom" },
  bathrooms: { title: "Bathrooms", singular: "Bathroom" },
  localities: { title: "Localities", singular: "Locality" },
  languages: { title: "Languages", singular: "Language" },
  amenities: { title: "Amenities", singular: "Amenity" },
  propertyTypes: { title: "Property types", singular: "Property type" },
  countries: { title: "Countries", singular: "Country" },
  cities: { title: "Cities", singular: "City" },
};

function MasterDataPanel({ resource }) {
  const title = TAB_META[resource]?.title || resource;
  const singular = TAB_META[resource]?.singular || title;
  const {
    ui,
    listHeaderSx,
    listPaperSx,
    listTitleSx,
    primaryBtnSx,
    statusChipSx,
    tableBodyCellSx,
    tableHeaderCellSx,
    tableRowHoverSx,
    tableSx,
    tableContainerSx,
  } = useAdminUi();

  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editId, setEditId] = useState(null);
  const [formError, setFormError] = useState("");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [countries, setCountries] = useState([]);
  const [countryFilterId, setCountryFilterId] = useState("");
  const [formCities, setFormCities] = useState([]);
  const [propertyCategory, setPropertyCategory] = useState("commercial");

  const columns = useMemo(() => {
    if (resource === "amenities") return ["Name", "Position", "Status", "Action"];
    if (resource === "localities") return ["Name", "Country", "City", "Coordinates", "Action"];
    if (resource === "propertyTypes" || resource === "countries") return ["Name", "Action"];
    if (resource === "cities") return ["Name", "Country", "Action"];
    return ["Name", "Status", "Action"];
  }, [resource]);

  const loadCountries = useCallback(async () => {
    try {
      const res = await fetchMasterDataAction("countries", { page: 1, perPage: 200 });
      const list = extractList(res);
      setCountries(list);
      return list;
    } catch {
      setCountries([]);
      return [];
    }
  }, []);

  const loadCitiesForCountry = useCallback(async (countryId) => {
    if (!countryId) {
      setFormCities([]);
      return [];
    }
    try {
      const res = await fetchMasterDataAction("cities", { page: 1, perPage: 200, country_id: countryId });
      const list = extractList(res);
      setFormCities(list);
      return list;
    } catch {
      setFormCities([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (resource !== "cities" && resource !== "localities") return;
    loadCountries();
  }, [resource, loadCountries]);

  useEffect(() => {
    if (resource !== "cities") return;
    let cancelled = false;
    (async () => {
      const list = await loadCountries();
      if (cancelled) return;
      const firstId = rowId(list[0]) || "";
      setCountryFilterId((prev) => prev || firstId);
    })();
    return () => {
      cancelled = true;
    };
  }, [resource, loadCountries]);

  useEffect(() => {
    if (resource !== "localities" || !formOpen) return;
    loadCitiesForCountry(form.country_id);
  }, [resource, formOpen, form.country_id, loadCitiesForCountry]);

  const load = useCallback(async () => {
    if (resource === "cities" && !countryFilterId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fetchParams =
        resource === "propertyTypes"
          ? { page: 1, perPage: 200, category: propertyCategory }
          : { page, perPage };
      if (resource === "cities") fetchParams.country_id = countryFilterId;
      const res = await fetchMasterDataAction(resource, fetchParams);
      const list = extractList(res);
      if (resource === "propertyTypes") {
        const filtered = list.filter((item) => item?.category === propertyCategory);
        const start = (page - 1) * perPage;
        setRows(filtered.slice(start, start + perPage));
        setTotal(filtered.length);
      } else {
        setRows(list);
        setTotal(extractTotal(res, list.length));
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || `Failed to load ${title.toLowerCase()}.`);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, resource, title, countryFilterId, propertyCategory]);

  useEffect(() => {
    setPage(1);
  }, [resource, countryFilterId, propertyCategory]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage) || 1);
  const canGoNext = page < totalPages && rows.length > 0;

  const countryOptions = useMemo(
    () => countries.map((c) => ({ id: rowId(c), name: c?.name || "—" })).filter((c) => c.id),
    [countries]
  );

  const cityOptions = useMemo(
    () => formCities.map((c) => ({ id: rowId(c), name: c?.name || "—" })).filter((c) => c.id),
    [formCities]
  );

  const openAdd = () => {
    setFormMode("add");
    setEditId(null);
    setForm({
      ...EMPTY_FORM,
      ...(resource === "cities" ? { country_id: countryFilterId } : {}),
      ...(resource === "propertyTypes" ? { category: propertyCategory } : {}),
    });
    setFormError("");
    setActionError("");
    setFormOpen(true);
  };

  const openEdit = (item) => {
    const id = rowId(item);
    if (!id) return;
    setFormMode("edit");
    setEditId(id);
    setForm(itemToForm(item, resource));
    setFormError("");
    setActionError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (busyId) return;
    setFormOpen(false);
    setEditId(null);
    setFormError("");
  };

  const submitForm = async () => {
    const validation = validateForm(resource, form);
    if (validation) {
      setFormError(validation);
      return;
    }
    setBusyId(editId || "new");
    setFormError("");
    try {
      const payload = buildPayload(resource, form, editId);
      if (formMode === "edit") {
        await editMasterDataAction(resource, payload);
      } else {
        await addMasterDataAction(resource, payload);
      }
      setFormOpen(false);
      setEditId(null);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.message || e?.message || "Save failed.");
    } finally {
      setBusyId(null);
    }
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
    setActionError("");
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setBusyId(deleteId);
    try {
      await deleteMasterDataAction(resource, deleteId);
      setDeleteOpen(false);
      setDeleteId(null);
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.message || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const updateField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country_id" && resource === "localities") {
        next.city_id = "";
      }
      return next;
    });
  };

  return (
    <Paper elevation={0} sx={listPaperSx}>
      <Box
        sx={{
          ...listHeaderSx,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
        }}
      >
        <Typography sx={listTitleSx}>{title}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          {resource === "cities" ? (
            <TextField
              select
              size="small"
              label="Country"
              value={countryFilterId}
              onChange={(e) => setCountryFilterId(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {countryOptions.length === 0 ? (
                <MenuItem value="">No countries</MenuItem>
              ) : (
                countryOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          ) : null}
          <Button variant="contained" color="primary" sx={primaryBtnSx} onClick={openAdd}>
            + Add {singular}
          </Button>
        </Stack>
      </Box>

      {resource === "propertyTypes" ? (
        <Box sx={{ px: 1.5, pt: 1.5 }}>
          <AdminTabBar
            tabs={PROPERTY_TYPE_TABS}
            value={propertyCategory}
            onChange={setPropertyCategory}
            fullWidth={false}
          />
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ m: 1 }}>
          {error}
        </Alert>
      ) : null}
      {actionError && !formOpen ? (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col} sx={tableHeaderCellSx}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                      No {title.toLowerCase()} found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((item) => {
                    const id = rowId(item);
                    const statusLabel = formatStatus(item?.status);
                    return (
                      <TableRow hover key={id || item?.name} sx={tableRowHoverSx}>
                        <TableCell sx={tableBodyCellSx}>{item?.name || "—"}</TableCell>
                        {resource === "amenities" ? (
                          <TableCell sx={tableBodyCellSx}>{item?.position ?? "—"}</TableCell>
                        ) : null}
                        {resource === "localities" ? (
                          <>
                            <TableCell sx={tableBodyCellSx}>{formatCountryLabel(item)}</TableCell>
                            <TableCell sx={tableBodyCellSx}>{formatCityLabel(item)}</TableCell>
                            <TableCell sx={tableBodyCellSx}>{formatCoordinates(item)}</TableCell>
                          </>
                        ) : null}
                        {resource === "cities" ? (
                          <TableCell sx={tableBodyCellSx}>{formatCountryLabel(item)}</TableCell>
                        ) : null}
                        {hasStatusField(resource) ? (
                          <TableCell sx={tableBodyCellSx}>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{ height: 26, fontSize: 13, ...statusChipSx(statusLabel) }}
                            />
                          </TableCell>
                        ) : null}
                        <TableCell sx={{ ...tableBodyCellSx, whiteSpace: "nowrap" }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton size="small" aria-label="Edit" onClick={() => openEdit(item)} disabled={!id}>
                              <EditOutlinedIcon sx={{ fontSize: 18, color: ui.iconView }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Delete"
                              onClick={() => id && openDelete(id)}
                              disabled={!id || busyId === id}
                            >
                              <DeleteOutlineOutlinedIcon sx={{ fontSize: 18, color: ui.iconDelete }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} flexWrap="wrap" spacing={1} sx={{ p: 1.2 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Typography sx={{ fontSize: 14, alignSelf: "center", color: ui.inkMuted }}>
              Page {page} {total ? `(${total} total)` : ""}
            </Typography>
            <Button size="small" disabled={!canGoNext} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </Stack>
        </>
      )}

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="xs">
        <DialogTitle>{formMode === "edit" ? `Edit ${singular}` : `Add ${singular}`}</DialogTitle>
        <DialogContent>
          {formError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {formError}
            </Alert>
          ) : null}
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField label="Name" fullWidth size="small" value={form.name} onChange={updateField("name")} />
            {resource === "propertyTypes" ? (
              <TextField
                select
                label="Category"
                fullWidth
                size="small"
                value={form.category}
                onChange={updateField("category")}
              >
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="residential">Residential</MenuItem>
              </TextField>
            ) : null}
            {resource === "amenities" ? (
              <TextField
                label="Position"
                fullWidth
                size="small"
                type="number"
                value={form.position}
                onChange={updateField("position")}
              />
            ) : null}
            {resource === "localities" ? (
              <>
                <TextField
                  select
                  label="Country"
                  fullWidth
                  size="small"
                  value={form.country_id}
                  onChange={updateField("country_id")}
                >
                  {countryOptions.length === 0 ? (
                    <MenuItem value="">No countries available</MenuItem>
                  ) : (
                    countryOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
                <TextField
                  select
                  label="City"
                  fullWidth
                  size="small"
                  value={form.city_id}
                  onChange={updateField("city_id")}
                  disabled={!form.country_id}
                >
                  {!form.country_id ? (
                    <MenuItem value="">Select a country first</MenuItem>
                  ) : cityOptions.length === 0 ? (
                    <MenuItem value="">No cities available</MenuItem>
                  ) : (
                    cityOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
                <TextField
                  label="Latitude"
                  fullWidth
                  size="small"
                  type="number"
                  value={form.latitude}
                  onChange={updateField("latitude")}
                />
                <TextField
                  label="Longitude"
                  fullWidth
                  size="small"
                  type="number"
                  value={form.longitude}
                  onChange={updateField("longitude")}
                />
              </>
            ) : null}
            {resource === "cities" ? (
              <TextField
                select
                label="Country"
                fullWidth
                size="small"
                value={form.country_id}
                onChange={updateField("country_id")}
              >
                {countryOptions.length === 0 ? (
                  <MenuItem value="">No countries available</MenuItem>
                ) : (
                  countryOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            ) : null}
            {hasStatusField(resource) ? (
              <TextField select label="Status" fullWidth size="small" value={form.status} onChange={updateField("status")}>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={!!busyId}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitForm} disabled={!!busyId}>
            {busyId ? <CircularProgress size={18} color="inherit" /> : formMode === "edit" ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !busyId && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete item</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>This cannot be undone. Continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={!!busyId}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={!!busyId}>
            {busyId ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default function MasterDataSettings() {
  const [activeTab, setActiveTab] = useState("bedrooms");

  return (
    <Stack spacing={2}>
      <AdminTabBar tabs={MASTER_TABS} value={activeTab} onChange={setActiveTab} variant="primary" />
      <MasterDataPanel key={activeTab} resource={activeTab} />
    </Stack>
  );
}
